/**
 * ConsentManager - Handles cookie consent state and analytics initialization
 * Receives consent from landing page (../ruut-landing) via localStorage or URL param
 */

const CONSENT_KEY = 'ruut_cookie_consent_v1';
const CONSENT_DATE_KEY = 'ruut_cookie_consent_date';

export class ConsentManager {
  constructor() {
    this.consent = {
      necessary: true,
      analytics: false,
      marketing: false,
    };
    this.listeners = [];
    this.initialized = false;
  }

  /**
   * Initialize consent manager and load saved preferences
   * First checks URL for cross-domain consent, then localStorage
   * If SKIP_ANALYTICS_CONSENT env var is set, grants all consent automatically
   */
  init() {
    // Check if consent override is enabled via env var
    if (window.skipAnalyticsConsent) {
      this.consent = {
        necessary: true,
        analytics: true,
        marketing: true,
      };
      this.initialized = true;
      return this.consent;
    }

    this.loadConsent();
    this.checkUrlConsent();
    this.setupEventListeners();
    this.initialized = true;
    return this.consent;
  }

  /**
   * Load saved consent from localStorage
   * Note: Uses same key as landing page for seamless experience
   */
  loadConsent() {
    try {
      const saved = localStorage.getItem(CONSENT_KEY);
      if (saved) {
        this.consent = { ...this.consent, ...JSON.parse(saved) };
      }
    } catch (e) {
      console.warn('[ConsentManager] Failed to load consent:', e);
    }
    return this.consent;
  }

  /**
   * Check URL for consent passed from landing page (cross-domain)
   * Landing page passes base64-encoded consent as ?consent=XXX parameter
   */
  checkUrlConsent() {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const consentParam = urlParams.get('consent');

      if (consentParam) {
        // Decode base64 consent from landing page
        const decodedConsent = JSON.parse(atob(consentParam));
        this.updateConsent(decodedConsent);

        // Clean up URL (remove consent param) without reloading
        const newUrl = new URL(window.location.href);
        newUrl.searchParams.delete('consent');
        window.history.replaceState({}, document.title, newUrl.toString());

        console.log('[ConsentManager] Received consent from landing page');
      }
    } catch (e) {
      console.warn('[ConsentManager] Failed to parse URL consent:', e);
    }
  }

  /**
   * Get current consent state
   */
  getConsent() {
    return { ...this.consent };
  }

  /**
   * Check if analytics is allowed
   */
  isAnalyticsAllowed() {
    if (window.skipAnalyticsConsent) return true;
    return this.consent.analytics === true;
  }

  /**
   * Check if marketing is allowed
   */
  isMarketingAllowed() {
    if (window.skipAnalyticsConsent) return true;
    return this.consent.marketing === true;
  }

  /**
   * Check if consent has been saved
   */
  hasConsentBeenSaved() {
    try {
      return localStorage.getItem(CONSENT_KEY) !== null;
    } catch (e) {
      return false;
    }
  }

  /**
   * Update consent and notify listeners
   */
  updateConsent(newConsent) {
    this.consent = { ...this.consent, ...newConsent };
    this.saveConsent();
    this.notifyListeners();
    this.applyConsentToVendors();
    return this.consent;
  }

  /**
   * Save consent to localStorage
   * Uses same key as landing page for cross-domain consistency
   */
  saveConsent() {
    try {
      localStorage.setItem(CONSENT_KEY, JSON.stringify(this.consent));
      localStorage.setItem(CONSENT_DATE_KEY, new Date().toISOString());
    } catch (e) {
      console.warn('[ConsentManager] Failed to save consent:', e);
    }
  }

  /**
   * Setup event listeners for consent changes
   */
  setupEventListeners() {
    // Listen for consent updates from banner
    window.addEventListener('cookie-consent-updated', (event) => {
      this.updateConsent(event.detail);
    });

    // Listen for vendor-specific consent requests
    window.addEventListener('analytics-consent-changed', (event) => {
      this.applyConsentToVendors();
    });
  }

  /**
   * Subscribe to consent changes
   * @param {Function} callback - Called when consent changes
   * @returns {Function} Unsubscribe function
   */
  onConsentChange(callback) {
    this.listeners.push(callback);
    // Immediately call with current state
    callback(this.getConsent());

    return () => {
      this.listeners = this.listeners.filter((cb) => cb !== callback);
    };
  }

  /**
   * Notify all listeners of consent change
   */
  notifyListeners() {
    this.listeners.forEach((callback) => {
      try {
        callback(this.getConsent());
      } catch (e) {
        console.error('[ConsentManager] Listener error:', e);
      }
    });
  }

  /**
   * Apply consent settings to analytics vendors
   */
  applyConsentToVendors() {
    // Google Analytics 4
    if (window.gtag) {
      window.gtag('consent', 'update', {
        analytics_storage: this.consent.analytics ? 'granted' : 'denied',
        ad_storage: this.consent.marketing ? 'granted' : 'denied',
        ad_user_data: this.consent.marketing ? 'granted' : 'denied',
        ad_personalization: this.consent.marketing ? 'granted' : 'denied',
      });
    }

    // Facebook Pixel
    if (window.fbq) {
      if (this.consent.marketing) {
        window.fbq('consent', 'grant');
      } else {
        window.fbq('consent', 'revoke');
      }
    }

    // Microsoft Clarity - requires cookie consent
    if (window.clarity) {
      // Clarity respects cookie consent automatically if configured
      // No explicit API for consent management
    }

    // Dispatch event for custom handling
    window.dispatchEvent(
      new CustomEvent('consent-state-changed', {
        detail: this.getConsent(),
      })
    );
  }

  /**
   * Reset consent (for testing)
   */
  resetConsent() {
    try {
      localStorage.removeItem(CONSENT_KEY);
      localStorage.removeItem(CONSENT_DATE_KEY);
    } catch (e) {
      console.warn('[ConsentManager] Failed to reset consent:', e);
    }
    this.consent = {
      necessary: true,
      analytics: false,
      marketing: false,
    };
    this.notifyListeners();
  }
}

// Singleton instance
export const consentManager = new ConsentManager();
export default consentManager;
